import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { UserService } from '@duck/api-definitions'
import { Badge, Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@duck/ui'
import { Link2 } from 'lucide-react'
import { apis, useTar } from '@/modules'

const statusVariant = (
  s: UserService.MyInvitationStatusEnum,
): 'default' | 'outline' | 'secondary' => {
  if (s === UserService.MyInvitationStatusEnum.Accepted) return 'default'
  if (s === UserService.MyInvitationStatusEnum.Expired) return 'secondary'
  return 'outline'
}

const statusKey = (
  s: UserService.MyInvitationStatusEnum,
): 'refer.table.statusPending' | 'refer.table.statusAccepted' | 'refer.table.statusExpired' => {
  if (s === UserService.MyInvitationStatusEnum.Accepted) return 'refer.table.statusAccepted'
  if (s === UserService.MyInvitationStatusEnum.Expired) return 'refer.table.statusExpired'
  return 'refer.table.statusPending'
}

export const MyInvitationsTable = () => {
  const { t, locale } = useTar()
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const copyLink = (id: string, url: string) => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id)
      setTimeout(() => setCopiedId((curr) => (curr === id ? null : curr)), 2000)
    })
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['myInvitations'],
    queryFn: async () => (await apis.userService.listMyInvitations()).data,
  })

  const fmt = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString(locale) : '—'

  if (isLoading) {
    return (
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t('refer.table.title')}</h2>
        <div className="bg-card border border-border rounded-md overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-secondary/5">
              <tr>
                {['headerName', 'headerStatus', 'headerSentAt', 'headerAcceptedAt'].map((k) => (
                  <th
                    key={k}
                    className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider"
                  >
                    {t(`refer.table.${k}` as Parameters<typeof t>[0])}
                  </th>
                ))}
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 5 }).map((__, j) => (
                    <td key={j} className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-secondary/20 rounded animate-pulse w-24" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    )
  }

  if (isError || !data) {
    return (
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t('refer.table.title')}</h2>
        <p className="text-muted-foreground">{t('refer.form.genericError')}</p>
      </section>
    )
  }

  if (data.length === 0) {
    return (
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t('refer.table.title')}</h2>
        <p className="text-muted-foreground">{t('refer.table.empty')}</p>
      </section>
    )
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{t('refer.table.title')}</h2>
      <div className="bg-card border border-border rounded-md overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary/5">
            <tr>
              <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('refer.table.headerName')}
              </th>
              <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('refer.table.headerStatus')}
              </th>
              <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('refer.table.headerSentAt')}
              </th>
              <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('refer.table.headerAcceptedAt')}
              </th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {data.map((row) => (
              <tr
                key={row.id}
                className="hover:bg-secondary/5"
                data-testid="my-invitation-row"
                data-email={row.email}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">
                          {[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent dir="ltr">{row.email}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge variant={statusVariant(row.status)} data-testid="my-invitation-status">
                    {t(statusKey(row.status))}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {fmt(row.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {fmt(row.acceptedAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-end">
                  {row.inviteUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => copyLink(row.id, row.inviteUrl!)}
                      data-testid="my-invitation-copy-link"
                      className="gap-1"
                    >
                      <Link2 className="w-4 h-4" />
                      {copiedId === row.id ? t('refer.form.copied') : t('refer.table.copyLink')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
